/**
 * WHICH DIRECTION THE RAIL'S SECOND LINE QUOTES — owner, 2026-09-13: Agent 忙碌並且沒有
 * 吐出來新的 Message Block 的時候, 我們可以將使用者的最後一個 Message 放在這個訊息預覽
 * 裡面, 並且可以設計這個訊息預覽上面有一個使用者的頭像.
 *
 * The line has always been what the agent SAID. A long turn produces no new speech
 * for minutes, so the row goes blank exactly when someone wants to know what they set
 * it off doing. While it is WORKING, the question is the news.
 *
 * Pure, and its own file, because it is a decision with four inputs and three
 * outcomes — and because the row it feeds needs half the rail's state to render, so
 * a test that went through the row would be testing the rail.
 */

export type PreviewInputs = {
  /** What the agent last said, and when (epoch seconds). */
  said?: string | null | undefined
  saidTs?: number | null | undefined
  /** What a person last typed at it, and when. Never an arrived message: the edge
   *  skips those, because this line is drawn under the account's own face. */
  ask?: string | null | undefined
  askTs?: number | null | undefined
  /** The PUSHED busy switch (presenceFor), never the polled row's `.status`. */
  working: boolean
  /** The last line this device saw for this agent, for the gap where the server has
   *  said nothing at all. */
  cached?: string | null | undefined
  /** Why the server sent no preview, when it said. */
  reason?: string | null | undefined
  /** False when the server sent no row for this agent at all this tick. */
  hasRow?: boolean | undefined
}

export type Preview = {
  text: string
  /** True when the text is the ACCOUNT'S own words — what draws its face beside them. */
  fromMe: boolean
}

const NONE: Preview = { text: '', fromMe: false }

export function choosePreview(i: PreviewInputs): Preview {
  /*
   * THE ASK WINS ONLY WHILE IT IS WORKING, AND ONLY WHILE IT IS THE NEWER OF THE TWO.
   *
   * Both halves matter. Without the first, a finished conversation would read as a
   * question nobody answered — the last thing typed outlives the answer in the
   * transcript. Without the second, an agent that answered and was then asked
   * something it has already replied to would show the stale question.
   *
   * `>=` rather than `>`: the two timestamps come from the same transcript and can
   * land in the same second, and in a tie the question is the one that started the
   * work now in progress.
   */
  if (i.working && i.ask) {
    if (!i.said || (i.askTs ?? 0) >= (i.saidTs ?? 0)) return { text: i.ask, fromMe: true }
  }
  if (i.said) return { text: i.said, fromMe: false }

  /*
   * NO ROW AT ALL is not an answer. The aggregator asks each mounted remote for its
   * own status and is best-effort: a host that is down, slow or mid-reconnect
   * contributes nothing for that tick. The reasons below are the server SAYING
   * something, and a blank is the honest render for those.
   */
  if (i.hasRow === false) return { text: i.cached ?? '', fromMe: false }
  if (i.reason === 'transcript_ambiguous') return { text: i.cached ?? '', fromMe: false }
  return NONE
}
